import { isSameDay } from 'date-fns'
import { useCallback } from 'react'

import { roomQueries } from '@/entities/room'
import { usePrefetchQuery } from '@/shared/api'
import { DateRange, OpenOptions, today, useDialog } from '@/shared/lib'

export const DIALOG = {
  CHECK_EXTEND_CONTRACT: 'CHECK_EXTEND_CONTRACT',
  IN_PROGRESS_CONTRACT: 'IN_PROGRESS_CONTRACT',
  END_DATE_CALENDAR: 'END_DATE_CALENDAR',
  PERIOD_CALENDAR: 'PERIOD_CALENDAR',
  BLOCKED_USER: 'BLOCKED_USER',
} as const

export type ContractDialog = keyof typeof DIALOG

type UseContractDialogProps = {
  roomId: number
  updatePeriod: (period: DateRange) => void
}

export const useContractDialog = ({
  roomId,
  updatePeriod,
}: UseContractDialogProps) => {
  const { prefetch } = usePrefetchQuery()

  const { isOpen, open, close, closeHandler } = useDialog<ContractDialog>()

  const getIsTodayBlocked = useCallback(async () => {
    const { success, data } = await prefetch(
      roomQueries.roomSchedules({
        roomId,
        year: today.getFullYear(),
        month: today.getMonth() + 1,
      }),
    )

    if (!success) {
      return
    }

    const isTodayBlocked = data.schedules.some(schedule =>
      isSameDay(schedule.date, today),
    )

    return isTodayBlocked
  }, [roomId])

  const handlePeriodDialogOpen = useCallback(async () => {
    const isTodayBlocked = await getIsTodayBlocked()

    if (isTodayBlocked) {
      updatePeriod({
        from: undefined,
        to: undefined,
      })
    }
  }, [getIsTodayBlocked])

  const openDialog = useCallback(
    async (key: ContractDialog, options?: OpenOptions) => {
      if (key === DIALOG.PERIOD_CALENDAR) {
        await handlePeriodDialogOpen()
      }

      open(key, options)
    },
    [handlePeriodDialogOpen, open],
  )

  return {
    isOpen,
    openDialog,
    close,
    closeHandler,
  }
}
